import { FC, ReactNode, useCallback, useMemo, useState } from 'react';

import { COMPOSER_MODES, COMPOSER_TYPES } from 'utils/constants';
import { SupportedFileInput } from '@uploadcare/upload-client';
import { COMMENT_TYPE, GmailThreads, OutlookMessage, Token } from 'utils/types';
import { useQueryParams } from 'hooks';

import ComposerPopover from './ComposerPopover';
import ComposerDealBody from './ComposerDealBody';
import ComposerTaskBody from './ComposerTaskBody';
import ComposerEventBody from './ComposerEventBody';
import ComposerEmail from './ComposerEmail';
import ComposerFieldBody from './ComposerFieldBody';
import ComposerLogActivity from './ComposerNotesLogActivity';

interface ComposerProps {
  type: COMPOSER_TYPES;
  isOpen: boolean;
  setThreads?: React.Dispatch<React.SetStateAction<GmailThreads>>;
  setMessages?: React.Dispatch<React.SetStateAction<OutlookMessage[]>>;
  closeComposer: () => void;
  onSubmit?: (data: {
    body: string;
    attachments: SupportedFileInput[];
    type: COMMENT_TYPE;
  }) => Promise<void>;
  isLoading?: boolean;
  title?: string;
  token?: Token;
  className?: string;
}

const Composer: FC<ComposerProps> = ({
  type,
  isOpen,
  closeComposer,
  setThreads,
  setMessages,
  title,
  token,
  className,
}: ComposerProps) => {
  const [queryParams] = useQueryParams();
  const [mode, setMode] = useState(COMPOSER_MODES.popover);
  const [attachments, setAttachments] = useState<any[]>([]);

  const commentType =
    (queryParams?.['type'] as COMMENT_TYPE) ?? COMMENT_TYPE.GENERAL;

  const toggleMode = () => {
    setMode((prevMode) =>
      prevMode === COMPOSER_MODES.popover
        ? COMPOSER_MODES.modal
        : COMPOSER_MODES.popover,
    );
  };

  const closeAndClearComposer = useCallback(() => {
    setAttachments([]);
    closeComposer();
  }, [closeComposer]);

  const bodyToRender: ReactNode = useMemo(() => {
    switch (type) {
      case COMPOSER_TYPES.note: {
        return (
          <ComposerLogActivity
            closeComposer={closeComposer}
            attachments={attachments}
            setAttachments={setAttachments}
            mode={mode}
            commentType={commentType}
          />
        );
      }
      case COMPOSER_TYPES.deal:
        return <ComposerDealBody mode={mode} />;
      case COMPOSER_TYPES.task:
        return <ComposerTaskBody mode={mode} />;
      case COMPOSER_TYPES.event:
        return <ComposerEventBody mode={mode} />;
      case COMPOSER_TYPES.email:
        return (
          <ComposerEmail
            setThreads={setThreads}
            setMessages={setMessages}
            attachments={attachments}
            setAttachments={setAttachments}
            closeComposer={closeComposer}
            token={token as Token}
          />
        );
      case COMPOSER_TYPES.field:
        return <ComposerFieldBody mode={mode} />;
    }
  }, [
    type,
    attachments,
    commentType,
    mode,
    setThreads,
    setMessages,
    closeComposer,
    token,
  ]);

  return (
    <ComposerPopover
      className={className}
      showHeader={![COMPOSER_TYPES.deal].includes(type)}
      closeComposer={closeAndClearComposer}
      toggleMode={toggleMode}
      mode={mode}
      isOpen={isOpen}
      type={type}
      body={bodyToRender}
      title={title}
      showCheckbox={[COMPOSER_TYPES.task].includes(type)}
    />
  );
};

export default Composer;
